import { User, TopActivity, MatchPriority } from '@/types';
import { API_CONFIG } from '@/config/api';

// Define AuthUser interface locally since it's not exported from types
interface AuthUser {
  id: string;
  username: string;
  // Null for phone-only accounts with no recovery_email on file.
  email: string | null;
  recovery_email?: string | null;
  has_password?: boolean;
  first_name?: string;
  last_name?: string;
  bio?: string;
  avatar_url?: string;
  gender?: 'male' | 'female' | 'non_binary' | 'prefer_not_to_say' | 'other';
  age?: number;
  profile_photos?: any[];
  categories_of_interest?: string[];
  location?: {
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  created_at: string;
  updated_at: string;
  // Top activities feature
  top_activities?: TopActivity[];
  match_priority?: MatchPriority;
  // Social media links
  instagram_url?: string;
  facebook_url?: string;
  x_url?: string;
  // Demographics
  ethnicity?: string;
}

export class UserApi {
  private static async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const baseUrl = API_CONFIG.baseUrl;
    
    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      let errorMessage = `${response.status}`;
      
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch {
        // If we can't parse the error response, use the status
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  static async searchUsers(query: string, options: { limit?: number; offset?: number; minAge?: number; maxAge?: number; gender?: string } = {}): Promise<{ users: User[]; total: number; hasMore: boolean }> {
    const params = new URLSearchParams({
      q: query,
      limit: (options.limit || 20).toString(),
      offset: (options.offset || 0).toString(),
    });

    // Add age filters if provided
    if (options.minAge !== undefined) {
      params.append('min_age', options.minAge.toString());
    }
    if (options.maxAge !== undefined) {
      params.append('max_age', options.maxAge.toString());
    }
    if (options.gender && options.gender !== 'any') {
      params.append('gender', options.gender);
    }

    try {
      const response = await this.request<{ users: AuthUser[]; total: number; hasMore: boolean }>(`/api/users/search?${params}`);
      
      const result = {
        users: response.users.map(this.convertAuthUserToUser),
        total: response.total,
        hasMore: response.hasMore
      };
      
      return result;
    } catch (error) {
      console.error('UserApi.searchUsers - error:', error);
      
      // TEMPORARY: Return mock data for testing if API endpoint doesn't exist
      console.log('Returning mock data for testing...');
      const mockUsers: User[] = [
        {
          id: 'mock-user-1',
          username: 'john_doe',
          email: 'john@example.com',
          firstName: 'John',
          lastName: 'Doe',
          bio: 'Adventure seeker and outdoor enthusiast',
          gender: 'male',
          profilePhotos: [],
          categoriesOfInterest: ['hiking', 'camping'],
          featuredActivityIds: [],
          location: {
            id: 'loc-1',
            name: 'Downtown',
            address: '123 Main St',
            city: 'New York',
            state: 'NY',
            country: 'USA',
            coordinates: { latitude: 40.7128, longitude: -74.0060 }
          },
          createdAt: new Date(),
          updatedAt: new Date()
        },
        {
          id: 'mock-user-2',
          username: 'jane_smith',
          email: 'jane@example.com',
          firstName: 'Jane',
          lastName: 'Smith',
          bio: 'Fitness lover and social butterfly',
          gender: 'female',
          profilePhotos: [],
          categoriesOfInterest: ['fitness', 'social'],
          featuredActivityIds: [],
          location: {
            id: 'loc-2',
            name: 'Uptown',
            address: '456 Oak Ave',
            city: 'New York',
            state: 'NY',
            country: 'USA',
            coordinates: { latitude: 40.7589, longitude: -73.9851 }
          },
          createdAt: new Date(),
          updatedAt: new Date()
        }
      ];
      
      return {
        users: mockUsers,
        total: mockUsers.length,
        hasMore: false
      };
    }
  }

  static async getUserById(userId: string): Promise<User> {
    const response = await this.request<AuthUser>(`/api/users/${userId}`);
    return this.convertAuthUserToUser(response);
  }

  static async getUserByUsername(username: string): Promise<User> {
    const response = await this.request<AuthUser>(`/api/users/by-username/${username}`);
    return this.convertAuthUserToUser(response);
  }

  static async getCurrentUser(): Promise<User> {
    const response = await this.request<AuthUser>('/api/users/me');
    return this.convertAuthUserToUser(response);
  }

  static async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    const response = await this.request<AuthUser>(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return this.convertAuthUserToUser(response);
  }

  static async updateCurrentUserProfile(updates: Partial<User>): Promise<User> {
    // Convert User type to backend format
    const backendUpdates: any = {};
    
    if (updates.firstName !== undefined) backendUpdates.first_name = updates.firstName;
    if (updates.lastName !== undefined) backendUpdates.last_name = updates.lastName;
    if (updates.bio !== undefined) backendUpdates.bio = updates.bio;
    // Avatar is now handled via profilePhotos
    if (updates.gender !== undefined) backendUpdates.gender = updates.gender;
    if ((updates as any).ethnicity !== undefined) backendUpdates.ethnicity = (updates as any).ethnicity;
    if (updates.age !== undefined) backendUpdates.age = updates.age;
    if (updates.categoriesOfInterest !== undefined) backendUpdates.categories_of_interest = updates.categoriesOfInterest;
    if (updates.profilePhotos !== undefined) backendUpdates.profile_photos = updates.profilePhotos;
    if (updates.location !== undefined) backendUpdates.location = updates.location;
    if (updates.topActivities !== undefined) backendUpdates.top_activities = updates.topActivities;
    if (updates.instagramUrl !== undefined) backendUpdates.instagram_url = updates.instagramUrl;
    if (updates.facebookUrl !== undefined) backendUpdates.facebook_url = updates.facebookUrl;
    if (updates.xUrl !== undefined) backendUpdates.x_url = updates.xUrl;
    if (updates.recoveryEmail !== undefined) backendUpdates.recovery_email = updates.recoveryEmail;

    const response = await this.request<AuthUser>('/api/users/me', {
      method: 'PUT',
      body: JSON.stringify(backendUpdates),
    });
    return this.convertAuthUserToUser(response);
  }

  static async deleteUser(userId: string): Promise<void> {
    await this.request(`/api/users/${userId}`, {
      method: 'DELETE',
    });
  }

  private static convertAuthUserToUser(authUser: AuthUser): User {
    return {
      id: authUser.id,
      username: authUser.username,
      email: authUser.email,
      recoveryEmail: authUser.recovery_email ?? null,
      hasPassword: authUser.has_password,
      firstName: authUser.first_name || undefined,
      lastName: authUser.last_name || undefined,
      bio: authUser.bio || undefined,
      gender: authUser.gender,
      ethnicity: authUser.ethnicity as any || undefined,
      age: authUser.age || undefined,
      profilePhotos: Array.isArray(authUser.profile_photos) ? authUser.profile_photos : (authUser.profile_photos ? [authUser.profile_photos] : []),
      categoriesOfInterest: authUser.categories_of_interest || [],
      featuredActivityIds: [],
      location: authUser.location ? {
        id: (authUser.location as any).id || '',
        name: (authUser.location as any).name || '',
        address: (authUser.location as any).address || '',
        city: (authUser.location as any).city || '',
        state: (authUser.location as any).state || '',
        country: (authUser.location as any).country || '',
        coordinates: (authUser.location as any).coordinates
      } : undefined,
      createdAt: new Date(authUser.created_at),
      updatedAt: new Date(authUser.updated_at),
      // Top activities feature
      topActivities: authUser.top_activities || [],
      matchPriority: authUser.match_priority || 'balanced',
      // Social media links
      instagramUrl: (authUser as any).instagram_url || undefined,
      facebookUrl: (authUser as any).facebook_url || undefined,
      xUrl: (authUser as any).x_url || undefined
    };
  }
}
